import React, { useContext, useEffect, useState, useRef } from 'react';
import { DataContext } from '../../Provider/dataProvider';
import { fetchWithAuth } from '../../utils/authUtils';
import '../../css/adminDashboard.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// Helper function to format date to Vietnam time
const formatVietnamDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (e) {
    return dateString;
  }
};

const AdminHealthLogs = () => {
  const { data, accessToken } = useContext(DataContext);
  const [logs, setLogs] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortField, setSortField] = useState('measuredAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const abortRef = useRef(null);

  
  // Debounce for search input to reduce jank
  useEffect(() => {
    const handler = setTimeout(() => {
      const value = searchInput.trim();
      if (value.length === 0 || value.length >= 2) {
        setSearch(value);
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    if (!accessToken) {
      setLoading(true);
      return;
    }
    loadHealthLogs();
  }, [accessToken, page, search, sortField, sortOrder]);

  const loadHealthLogs = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        sort: sortField,
        order: sortOrder,
      });

      if (search) params.append('search', search);

      // Abort previous request if exists
      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();

      const response = await fetchWithAuth(`${API_BASE}/api/admin/health-logs?${params}`, { signal: abortRef.current.signal });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.message || 'Không thể tải dữ liệu sức khỏe');
      }

      const fetchedLogs = Array.isArray(payload?.data?.logs) ? payload.data.logs : [];
      setAllLogs(fetchedLogs);

      const grouped = {};
      fetchedLogs.forEach(log => {
        if (!grouped[log.id]) {
          grouped[log.id] = log;
        } else {
          const current = grouped[log.id];
          const candidate = log;
          const metricPriority = {
            bloodPressure: 1,
            heartRate: 2,
            bloodSugar: 3,
            temperature: 4,
            weight: 5
          };
          const currentPrio = metricPriority[current.metric] || 99;
          const candidatePrio = metricPriority[candidate.metric] || 99;
          const currentAbnormal = current.isAbnormal ? 1 : 0;
          const candidateAbnormal = candidate.isAbnormal ? 1 : 0;
          if (candidateAbnormal > currentAbnormal || (candidateAbnormal === currentAbnormal && candidatePrio < currentPrio)) {
            grouped[log.id] = candidate;
          }
        }
      });
      const arr = Object.values(grouped);
      // Ensure client-side sort by selected datetime field for stable UX
      arr.sort((a, b) => {
        const av = a[sortField] || a.createdAt;
        const bv = b[sortField] || b.createdAt;
        const ad = av ? new Date(av).getTime() : 0;
        const bd = bv ? new Date(bv).getTime() : 0;
        return sortOrder === 'asc' ? (ad - bd) : (bd - ad);
      });
      setLogs(arr);
      setTotalPages(payload?.data?.totalPages || 1);
    } catch (err) {
      if (err?.name === 'AbortError') {
        // ignore aborted requests
        return;
      }
      setError(err?.message || 'Lỗi khi tải dữ liệu sức khỏe');
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleViewDetail = (log) => {
    const allRowsForId = allLogs.filter(l => l.id === log.id);
    const full = {
      id: log.id,
      accountId: log.accountId,
      elderlyName: log.elderlyName,
      elderlyEmail: log.elderlyEmail,
      createdAt: log.createdAt,
      measuredAt: log.measuredAt,
      notes: log.notes,
      systolic: null,
      diastolic: null,
      heartRate: null,
      bloodSugar: null,
      temperature: null,
      weight: null,
      isAbnormal: false
    };
    allRowsForId.forEach(row => {
      if (row.metric === 'bloodPressure') {
        const parts = row.value.split('/');
        full.systolic = parts[0] ? Number(parts[0]) : null;
        full.diastolic = parts[1] ? Number(parts[1]) : null;
        if (row.isAbnormal) full.isAbnormal = true;
      } else if (row.metric === 'heartRate') {
        full.heartRate = Number(row.value);
        if (row.isAbnormal) full.isAbnormal = true;
      } else if (row.metric === 'bloodSugar') {
        full.bloodSugar = Number(row.value);
        if (row.isAbnormal) full.isAbnormal = true;
      } else if (row.metric === 'temperature') {
        full.temperature = Number(row.value);
        if (row.isAbnormal) full.isAbnormal = true;
      } else if (row.metric === 'weight') {
        full.weight = Number(row.value);
      }
    });
    setSelectedLog(full);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa bản ghi này?')) return;

    try {
      const response = await fetchWithAuth(`${API_BASE}/api/admin/health-logs/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || 'Không thể xóa bản ghi');
      }

      loadHealthLogs();
    } catch (err) {
      alert(err?.message || 'Lỗi khi xóa bản ghi');
    }
  };

  const getMetricLabel = (metric) => {
    const labels = {
      bloodPressure: 'Huyết áp',
      heartRate: 'Nhịp tim',
      bloodSugar: 'Đường huyết',
      weight: 'Cân nặng',
      temperature: 'Nhiệt độ',
      oxygen: 'Độ bão hòa O2',
    };
    return labels[metric] || metric;
  };

  if (loading && !initialized) {
    return (
      <div className="admin-page-container">
        <div className="admin-loading">
          <i className="fa-solid fa-spinner fa-spin"></i>
          <p>Đang tải dữ liệu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-container">
      <div className="admin-header">
        <div>
          <h1><i className="fa-solid fa-heart-pulse"></i> Quản lý dữ liệu sức khỏe</h1>
          <small>Quản lý và giám sát các chỉ số sức khỏe của người cao tuổi</small>
        </div>
      </div>

      <div className="admin-table-card">
        <div className="admin-table-header">
          <div className="admin-search-box">
            <input
              type="text"
              placeholder="Tìm kiếm theo tên, email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <i className="fa-solid fa-search"></i>
          </div>
          <div className="admin-filters">
            <label style={{ marginRight: 8 }}>Sắp xếp:</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value === 'asc' ? 'asc' : 'desc')}
            >
              <option value="desc">Mới nhất</option>
              <option value="asc">Cũ nhất</option>
            </select>
            {loading && initialized && (
              <small style={{ marginLeft: 12, color: '#7f8c8d' }}>
                <i className="fa-solid fa-spinner fa-spin"></i> Đang tải...
              </small>
            )}
          </div>
        </div>

        {error && (
          <div className="admin-error">
            <i className="fa-solid fa-circle-exclamation"></i>
            <p>{error}</p>
          </div>
        )}

        <table className="admin-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('measuredAt')}>
                Ngày {sortField === 'measuredAt' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('elderlyName')}>
                Người cao tuổi {sortField === 'elderlyName' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('metric')}>
                Chỉ số {sortField === 'metric' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th>Giá trị</th>
              <th>Đơn vị</th>
              <th>Ghi chú</th>
              <th>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                  Không có dữ liệu
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.rowKey || `${log.id}-${log.metric}`}>
                  <td>{formatVietnamDate(log.measuredAt || log.createdAt)}</td>
                  <td>
                    <strong>{log.elderlyName || log.fullName || 'N/A'}</strong>
                    <br />
                    <small style={{ color: '#7f8c8d' }}>{log.elderlyEmail || log.email || ''}</small>
                  </td>
                  <td>
                    <span className="admin-badge admin-badge-info">
                      {getMetricLabel(log.metric)}
                    </span>
                  </td>
                  <td>
                    <strong style={{ color: log.isAbnormal ? '#ff4757' : '#2f3542' }}>
                      {log.value}
                    </strong>
                  </td>
                  <td>{log.unit || ''}</td>
                  <td>{log.notes || '-'}</td>
                  <td className="admin-action-links">
                    <button
                      className="admin-btn admin-btn-view"
                      onClick={() => handleViewDetail(log)}
                    >
                      <i className="fa-solid fa-eye"></i> Chi tiết
                    </button>
                    <button
                      className="admin-btn admin-btn-delete"
                      onClick={() => handleDelete(log.id)}
                    >
                      <i className="fa-solid fa-trash"></i> Xóa
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="admin-pagination">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <i className="fa-solid fa-chevron-left"></i> Trước
            </button>
            <span>Trang {page} / {totalPages}</span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Sau <i className="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        )}
      </div>

      {showModal && selectedLog && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Chi tiết bản ghi sức khỏe</h3>
              <button className="admin-modal-close" onClick={() => setShowModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-info-grid">
                <div className="admin-info-item">
                  <label>ID</label>
                  <span>#{selectedLog.id}</span>
                </div>
                <div className="admin-info-item">
                  <label>Người cao tuổi</label>
                  <span>{selectedLog.elderlyName || selectedLog.fullName || 'N/A'}</span>
                </div>
                <div className="admin-info-item">
                  <label>Email</label>
                  <span>{selectedLog.elderlyEmail || selectedLog.email || '-'}</span>
                </div>
                <div className="admin-info-item">
                  <label>Ngày ghi</label>
                  <span>{formatVietnamDate(selectedLog.createdAt)}</span>
                </div>
                {((selectedLog.systolic != null) || (selectedLog.diastolic != null)) && (
                  <div className="admin-info-item" style={{ gridColumn: 'span 2' }}>
                    <label>Huyết áp</label>
                    <span><strong>{selectedLog.systolic ?? '-'} / {selectedLog.diastolic ?? '-'}</strong> mmHg</span>
                  </div>
                )}
                {selectedLog.heartRate != null && (
                  <div className="admin-info-item">
                    <label>Nhịp tim</label>
                    <span><strong>{selectedLog.heartRate}</strong> bpm</span>
                  </div>
                )}
                {selectedLog.bloodSugar != null && (
                  <div className="admin-info-item">
                    <label>Đường huyết</label>
                    <span><strong>{selectedLog.bloodSugar}</strong> mmol/L</span>
                  </div>
                )}
                {selectedLog.temperature != null && (
                  <div className="admin-info-item">
                    <label>Nhiệt độ</label>
                    <span><strong>{selectedLog.temperature}</strong> °C</span>
                  </div>
                )}
                {selectedLog.weight != null && (
                  <div className="admin-info-item">
                    <label>Cân nặng</label>
                    <span><strong>{selectedLog.weight}</strong> kg</span>
                  </div>
                )}
                {selectedLog.isAbnormal && (
                  <div className="admin-info-item" style={{ gridColumn: 'span 2' }}>
                    <label>Trạng thái</label>
                    <span style={{ color: '#ff4757', fontWeight: 'bold' }}>
                      ⚠️ Bất thường
                    </span>
                  </div>
                )}
                {selectedLog.notes && (
                  <div className="admin-info-item" style={{ gridColumn: 'span 2' }}>
                    <label>Ghi chú</label>
                    <span>{selectedLog.notes}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHealthLogs;
